"use client";

import { Citrus, FlaskConical, Barrel, Wine } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "REQUEST QUOTE",
  Icon: Citrus,
  description: "Submit your requirements with procurement confidence. Our system ensures your specifications are captured accurately for a precise initial assessment."
}, {
  number: "02",
  title: "QUOTATION REVIEW & NEGOTIATION",
  Icon: FlaskConical,
  description: "Receive and analyze detailed quotes. We facilitate transparent quote processing and professional negotiation to align with your budgetary goals."
}, {
  number: "03",
  title: "ORDER CONFIRMATION & PAYMENT",
  Icon: Barrel,
  description: "Finalize your procurement with payment security. We provide robust transaction protocols and formal order confirmation for complete peace of mind."
}, {
  number: "04",
  title: "DELIVERY & DOCUMENTATION",
  Icon: Wine,
  description: "Seamless logistics meets comprehensive export documentation. We manage the final mile and all necessary paperwork for international compliance."
}];
export default function Process() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3883" id="how-it-works" className="bg-[var(--color-background)] text-[var(--color-foreground)] relative overflow-hidden py-32 md:py-[200px]">
      {}
      <div className="pointer-events-none absolute top-0 left-4 hidden h-full lg:left-8 lg:block">
        <div className="sticky top-32">
          <div className="font-sans text-[10px] font-medium tracking-[0.3em] uppercase text-[var(--color-foreground)]/50" style={{
          writingMode: "vertical-rl"
        }}>
            00 &mdash; OUR WORKFLOW
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-20">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="md:col-span-7">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-[var(--color-accent-500)]" />
              <span className="font-sans text-xs font-medium tracking-[0.25em] uppercase text-[var(--color-accent-500)]">
                B2B Buying Process
              </span>
            </div>
            <h2 className="font-default italic text-4xl leading-[1.05] tracking-tight text-[var(--color-foreground)] sm:text-5xl md:text-6xl lg:text-[64px]">
              B2B Buying Process
              <br />
              <span className="text-[var(--color-foreground)]/70">
                Simplified Procurement Excellence
              </span>
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-6">
            <p className="font-default text-lg leading-relaxed text-[var(--color-foreground)]/75 md:text-xl">
              At our core, we believe in systematic excellence. We execute projects in focused sprints, ensuring every detail is refined through rigorous testing and strategic iteration.
            </p>
            <p className="mt-5 font-default text-base leading-relaxed text-[var(--color-foreground)]/60">
              No wasted effort. No technical debt. Only the deliberate application of expertise to solve complex business challenges.
            </p>
          </div>
        </div>

        {}
        <div className="relative mt-20 md:mt-28">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {steps.map((step, i) => {
            const card = useRevealOnScroll<HTMLDivElement>({
              threshold: 0.15
            });
            const Icon = step.Icon;
            return <div key={step.number} data-index={i} ref={card.ref} className={`group relative transition-all duration-700 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  {}
                  {i < steps.length - 1 && <div className="pointer-events-none absolute top-1/2 -right-3 z-20 hidden lg:block">
                      <div className="flex items-center gap-0.5">
                        <span className="h-px w-1.5 bg-[var(--color-accent-500)]" />
                        <span className="h-px w-1 bg-[var(--color-accent-500)]/60" />
                        <span className="h-px w-0.5 bg-[var(--color-accent-500)]/30" />
                      </div>
                    </div>}

                  {}
                  <div className="relative h-[380px] overflow-hidden border border-[var(--color-primary-200)] bg-white/50 backdrop-blur-sm shadow-md transition-all duration-500 group-hover:-translate-y-1 group-hover:border-[var(--color-accent-500)] group-hover:shadow-lg">
                    {}
                    <div className="absolute -top-4 -left-2 select-none">
                      <span className="font-sans text-[120px] font-extrabold leading-none tracking-tighter text-[var(--color-foreground)]/5 transition-colors duration-[400ms] group-hover:text-[var(--color-accent-500)]/20" aria-hidden="true">
                        {step.number}
                      </span>
                    </div>

                    {}
                    <div className="relative flex h-full flex-col justify-between p-8 pt-32">
                      <Icon className="h-10 w-10 text-[var(--color-primary-500)] transition-colors duration-300 group-hover:text-[var(--color-accent-500)]" strokeWidth={1} />

                      <div>
                        <div className="mb-4 h-px w-8 bg-[var(--color-primary-300)] transition-all duration-500 group-hover:w-16 group-hover:bg-[var(--color-accent-500)]" />
                        <h3 className="font-default font-sans text-2xl font-bold tracking-[0.15em] uppercase text-[var(--color-foreground)]">
                          {step.title}
                        </h3>
                        <p className="mt-4 font-default text-[15px] leading-relaxed text-[var(--color-foreground)]/65">
                          {step.description}
                        </p>
                      </div>
                    </div>

                    {}
                    <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--color-accent-500)] to-transparent" />
                    </div>
                  </div>
                </div>;
          })}
          </div>

          {}
          <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-[var(--color-primary-200)] pt-8 sm:flex-row sm:items-center">
            <p className="font-sans text-xs tracking-[0.25em] uppercase text-[var(--color-foreground)]/50">
              Four phases &mdash; one standard
            </p>
            <p className="font-default italic text-sm text-[var(--color-foreground)]/60">
              Delivered with absolute commitment.
            </p>
          </div>
        </div>
      </div>
    </section>;
}